/**
 * Map filters based on stored device configuration (configuration.ap /
 * configuration.treemgmt), not live state reports (private_wifi, ul_band, etc.).
 */

import { isCapDevice } from './publicWifiRrmPlanUi';

function bandFromRadioName(name) {
  const lower = String(name || '').trim().toLowerCase();
  if (lower.startsWith('2.4ghz')) return '2.4GHz';
  if (lower.startsWith('5ghz')) return '5GHz';
  if (lower.startsWith('6ghz')) return '6GHz';
  return '';
}

export function getDeviceConfiguration(device) {
  const cfg = device?.configuration;
  return cfg && typeof cfg === 'object' ? cfg : null;
}

/** True when configuration.ap has an entry targeting a 5 GHz radio slot. */
export function deviceHas5GHzApInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  if (!cfg) return false;
  const apRaw = cfg.ap;
  if (!Array.isArray(apRaw)) return false;
  return apRaw.some((ap) => {
    if (!ap || typeof ap !== 'object') return false;
    return bandFromRadioName(ap.radio) === '5GHz';
  });
}

function parseTreemgmtMaxChildren(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const raw = entry.max_children;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * True when at least one treemgmt entry allows mesh children
 * (max_children unset or non-zero).
 */
export function deviceAllowsChildrenInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  const treemgmt = cfg?.treemgmt;
  if (!Array.isArray(treemgmt) || treemgmt.length === 0) return false;
  return treemgmt.some((entry) => {
    const maxChildren = parseTreemgmtMaxChildren(entry);
    return maxChildren === null || maxChildren !== 0;
  });
}

/**
 * True when at least one treemgmt entry explicitly blocks mesh children
 * (max_children === 0).
 */
export function deviceBlocksChildrenInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  const treemgmt = cfg?.treemgmt;
  if (!Array.isArray(treemgmt) || treemgmt.length === 0) return false;
  return treemgmt.some((entry) => parseTreemgmtMaxChildren(entry) === 0);
}

export function devicePassesFiveGApConfigFilter(device, filter) {
  if (!filter || filter === 'all') return true;
  const hasAp = deviceHas5GHzApInConfig(device);
  if (filter === 'yes') return hasAp || isCapDevice(device);
  if (filter === 'no') return !hasAp;
  return true;
}

export function devicePassesChildrenAllowedConfigFilter(device, filter) {
  if (!filter || filter === 'all') return true;
  if (filter === 'yes') return deviceAllowsChildrenInConfig(device);
  if (filter === 'no') return deviceBlocksChildrenInConfig(device);
  return true;
}

/**
 * True when the device's 5 GHz radio slot is used for Public WiFi:
 * configuration.ap[] has an entry on a 5 GHz radio (e.g. 5GHz_0) AND
 * the corresponding treemgmt entry has max_children = 0 (mesh blocked —
 * which is what SSID assign does when it takes over the slot).
 *
 * Backhaul-only changes (tx_power_down, band, htmode) must NOT be
 * applied to these devices because changing the AP radio config would
 * affect the Public WiFi service, not the backhaul mesh.
 */
export function device5GHzRadioUsedForPublicWifi(device) {
  const cfg = getDeviceConfiguration(device);
  if (!cfg) return false;

  const apRaw = cfg.ap;
  if (!Array.isArray(apRaw)) return false;

  const fiveGRadioSlots = new Set();
  for (const ap of apRaw) {
    if (!ap || typeof ap !== 'object') continue;
    const radio = String(ap.radio || '').trim();
    if (bandFromRadioName(radio) === '5GHz') {
      fiveGRadioSlots.add(radio.toLowerCase());
    }
  }
  if (fiveGRadioSlots.size === 0) return false;

  const treemgmt = cfg.treemgmt;
  if (!Array.isArray(treemgmt)) return false;

  return treemgmt.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const treeId = String(entry.tree_id || '').trim().toLowerCase();
    if (!fiveGRadioSlots.has(treeId)) return false;
    const maxChildren = parseTreemgmtMaxChildren(entry);
    return maxChildren === 0;
  });
}

/** Lowercased serial set for a custom-tree / segment roster. */
export function segmentMemberSerials(tree) {
  const set = new Set();
  (tree?.devices || []).forEach((s) => {
    if (s) set.add(String(s).toLowerCase());
  });
  (tree?.caps || []).forEach((s) => {
    if (s) set.add(String(s).toLowerCase());
  });
  return set;
}

export function deviceBelongsToSegmentTree(device, tree) {
  const serial = device?.serial_number?.toLowerCase();
  if (!serial || !tree) return false;
  return segmentMemberSerials(tree).has(serial);
}

/** Map filter: all | custom-tree id. */
export function devicePassesSegmentFilter(device, segmentFilterId, customTrees) {
  if (!segmentFilterId || segmentFilterId === 'all') return true;
  const trees = Array.isArray(customTrees) ? customTrees : [];
  const tree = trees.find((t) => t?.id === segmentFilterId);
  if (!tree) return true;
  return deviceBelongsToSegmentTree(device, tree);
}
